import React, {
  memo,
  Suspense,
  useCallback,
  useMemo,
  lazy,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Map } from 'immutable';
import isEqual from 'lodash/isEqual';
import { parse } from 'query-string';
import { useLocation } from 'react-router-dom-v5-compat';
import { useParams } from 'react-router-dom';

import {
  selectIsLoginComplete,
  selectCurrentOrgParentId,
} from 'routes/AppContainer/selectors';
import {
  selectCurrentOrgId,
  selectProcessedScopes,
  selectAccessTokenScopes,
} from 'global/accessToken/selectors';
import { useInjectSagas } from 'utils/injectSaga';
import { useInjectReducers } from 'utils/injectReducer';
import { Loader } from 'components/Loader';
import { getRawMenuItems, getItemConfig } from 'config/utils';
import {
  getActiveScopes,
  checkScope,
  substituteOrgIdInScope,
} from 'utils/redirects';
import { scopeCodesToDescriptions } from 'utils/scopeDescriptions';
import NotFoundPage from 'pages/NotFoundPage/NotFoundPage';

import { RESTART_ON_REMOUNT } from './constants';

// set this to true if you want to see ALL routing debug info in console
const SHOW_ALL_ROUTE_DEBUG_INFO = false;

// Stable empty array reference to prevent unnecessary re-renders
const EMPTY_SCOPE_ARRAY = [];

// Helper function to check if a route is a container route
const isContainerRoute = (routeName) => {
  return (
    routeName &&
    ['authenticated', 'app', 'orgContainer', 'masterContainer'].includes(
      routeName,
    )
  );
};

// Helper function to build route object if missing
const buildRouteIfMissing = (route, routeProps) => {
  if (!route) {
    return {
      route: {
        RouteComponent: routeProps.RouteComponent,
        reducers: routeProps.reducers,
        sagas: routeProps.sagas,
        scope: routeProps.scope,
        path: routeProps.path,
        name: routeProps.name,
      },
      name: routeProps.name,
    };
  }
  return route;
};

// Helper function to select route component from array (A/B testing)
const selectRouteFromArray = (RouteComponent, location) => {
  if (!Array.isArray(RouteComponent)) {
    return RouteComponent;
  }

  let chosenRoute = RouteComponent[0];
  if (location.search) {
    const parsedValues = parse(location.search);
    if (parsedValues.beta) {
      if (parsedValues.beta > RouteComponent.length - 1) {
        parsedValues.beta = RouteComponent.length - 1;
      }
      chosenRoute = RouteComponent[parsedValues.beta];
    }
  }
  return chosenRoute;
};

export const WrappedRoute = memo(
  ({
    debug = SHOW_ALL_ROUTE_DEBUG_INFO, // BOOL - set this to true to print additional info, helpful for debugging new routes
    noAuth = false, // BOOL - set this to true to allow the route to mount without a login check
    RouteComponent = () => null, // FUNCTION - this is the react component we want to render for the route
    RouteImportPath, // ???
    route, // ???
    reducers = [], // ARRAY of objects with keys [key, reducer] - the reducers to inject when this route is mounted
    sagas = [], // ARRAY of objects with keys [key, saga] - the sagas to inject when this route is mounted
    scope = [], // ARRAY of strings that map to permissions required to view this route
    path, // STRING - the path of the route, i.e. 'users' to match
    name, // usually the PAGE variable for this route
    extraData = {}, // ??? additional data
  }) => {
    // Selectors
    const isLoginComplete = useSelector((state) =>
      selectIsLoginComplete()(state),
    );
    const currentOrg = useSelector((state) => selectCurrentOrgId()(state));
    const scopes = useSelector((state) => selectAccessTokenScopes()(state));
    const processedScopes = useSelector((state) =>
      selectProcessedScopes()(state),
    );
    const parentOrgId = useSelector((state) =>
      selectCurrentOrgParentId()(state),
    );

    const location = useLocation();
    const params = useParams();

    const originalProps = {
      RouteComponent,
      reducers,
      sagas,
      path,
      name,
      params,
      location: {
        ...location,
        query: parse(location.search),
      },
    };

    // ??? For some reason, slideouts don't get a route (but standard routes do)
    // so we rebuild it. Seems Curt wrote this line so check with him
    // eslint-disable-next-line no-param-reassign
    route = buildRouteIfMissing(route, {
      RouteComponent,
      reducers,
      sagas,
      scope,
      path,
      name,
    });

    // helper function so we only have to disable one line for eslint to allow console
    // you can use `debugLog` internally to print stuff and it'll be gated by the `debug` prop
    const debugLog = useCallback(
      (log) => {
        if (!debug) return;
        console.debug(`[createRoute] (${route.name}) - ${log}`); // eslint-disable-line no-console
      },
      [debug, route.name],
    );

    // State
    const [areReducersInjected, setAreReducersInjected] = useState(false);
    const [areSagasInjected, setAreSagasInjected] = useState(false);
    debugLog(
      `*** SETTING INITIAL STATE? areReducersInjected: ${areReducersInjected} areSagasInjected: ${areSagasInjected}`,
    );

    debugLog(`!!! route.name: ${route.name} (Wrapped Route)`);

    // Auto-lookup scopes from rawMenuItems if scope is not provided
    const resolvedScope = useMemo(() => {
      // Normalize empty arrays to use stable reference
      if (scope.length === 0) {
        // Try to lookup from rawMenuItems if scope is empty
        if (path) {
          // Extract base route (handle dynamic paths)
          const baseRoute = Array.isArray(path)
            ? path[0]
            : path.split('/:')[0].split('?')[0];

          const menuItem = getItemConfig(getRawMenuItems(), baseRoute);
          return menuItem?.scope || EMPTY_SCOPE_ARRAY;
        }
        return EMPTY_SCOPE_ARRAY;
      }

      // If scope has values, use it
      return scope;
    }, [scope, path]);

    // Synchronous scope checking for permission validation
    // This allows us to conditionally render NotFoundPage without redirecting
    const hasPermission = useMemo(() => {
      // Skip permission check for non-authed routes
      if (noAuth) {
        return true;
      }

      // Skip check for container routes (same as async check behavior)
      if (isContainerRoute(route?.name)) {
        return true;
      }

      // If no scope requirement, allow access
      if (!resolvedScope || resolvedScope.length === 0) {
        return true;
      }

      // If scopes aren't loaded yet, we can't determine permission
      // Return null to indicate loading state
      if (!scopes || !isLoginComplete) {
        return null;
      }

      // Calculate active scopes using the same logic as the saga
      // processedScopes is an Immutable Map, so we need to check if it exists and has the parentOrgId key
      const parentOrgScope =
        processedScopes && parentOrgId && Map.isMap(processedScopes)
          ? processedScopes.get(String(parentOrgId))
          : null;
      const activeScopes = getActiveScopes(currentOrg, scopes, parentOrgScope);

      // Check permissions using checkScope (same as saga)
      return checkScope(currentOrg, activeScopes, resolvedScope);
    }, [
      noAuth,
      route,
      resolvedScope,
      scopes,
      currentOrg,
      processedScopes,
      parentOrgId,
      isLoginComplete,
    ]);

    // inject our reducers dynamically on route mount
    // callback helps with knowing when we're done injecting
    const injectorReadyCallback = useCallback(() => {
      debugLog(`!!! injected reducers: done ${Date.now()}`);
      setAreReducersInjected(true);
    }, [debugLog]);
    useInjectReducers(reducers, injectorReadyCallback);

    // inject the sagas dynamically on route mount
    // callback helps with knowing when we're done injecting
    const newSagas = sagas.map((s) => ({
      ...s,
      mode: s.mode || RESTART_ON_REMOUNT,
    }));
    const sagaReadyCallback = useCallback(() => {
      debugLog(`!!! injected sagas: done ${Date.now()}`);
      setAreSagasInjected(true);
    }, [debugLog]);
    useInjectSagas(newSagas, sagaReadyCallback, route.name);

    useEffect(() => {
      debugLog('**** COMPONENT DID MOUNT ' + route.name);
      return () => {
        debugLog('**** COMPONENT WILL UNMOUNT ' + route.name);
        setAreReducersInjected(false);
        setAreSagasInjected(false);
      };
    }, [debugLog, route.name]);

    // This allows for memoization of the route component so that it does not unmount when the parent rerenders (due to being wrapped in Suspense)
    const ImportedRouteComponent = useMemo(() => {
      switch (RouteImportPath) {
        case 'AuthenticatedContainer':
          return lazy(() => import('routes/AuthenticatedContainer/index'));
        case 'OrgContainer':
          return lazy(() => import('routes/OrgContainer/index'));
        case 'EntrySchedules/EntrySchedulesCreateEditPage':
          return lazy(
            () =>
              import(
                'routes/EntrySchedules/EntrySchedulesCreateEditPage/index'
              ),
          );
        default:
          return null;
      }
    }, [RouteImportPath]);
    // NOTE - the array version of RouteComponent will get weird when trying to memoize which we need to do to prevent unmounting of the ChosenRoute within Suspense when the parent rerenders
    // Note: Having an array of RouteComponents breaks with our new drawer implementation
    const selectedRouteComponent = selectRouteFromArray(
      RouteComponent,
      location,
    );
    const ChosenRoute = ImportedRouteComponent || selectedRouteComponent;

    // Make sure injectors have finished and our login restoration is complete
    // (prevents null orgId from existing in later sagas)
    const loginCheck = noAuth || isLoginComplete;
    debugLog(
      `*** loginCheck: ${loginCheck} :: areSagasInjected: ${areSagasInjected} :: areReducersInjected: ${areReducersInjected} `,
    );
    if (!(areReducersInjected && areSagasInjected && loginCheck)) {
      debugLog(
        `[Delay Loading Route (waiting for injections)...] ${route.name} injected reducers?: ${areReducersInjected} - injected sagas?: ${areSagasInjected} - login complete? ${loginCheck}`,
      );
      return 'Loading...';
    }

    // Handle loading state when scopes aren't available yet
    if (hasPermission === null) {
      debugLog(
        `[Waiting for scopes to load...] ${route.name} - scopes: ${scopes ? 'loaded' : 'loading'}`,
      );
      return (
        <div style={{ marginTop: '55px' }}>
          <Loader loaderContentOnly />
        </div>
      );
    }

    // If user doesn't have permissions, render NotFoundPage without redirecting
    // This preserves the original URL in the browser address bar
    if (hasPermission === false) {
      debugLog(
        `[Permission denied - showing NotFoundPage] ${route.name} - resolvedScope: ${JSON.stringify(resolvedScope)}`,
      );
      let baseRoute;
      if (!path) {
        baseRoute = route?.name;
      } else if (Array.isArray(path)) {
        baseRoute = path[0];
      } else {
        baseRoute = path.split('/:')[0].split('?')[0];
      }
      const menuItem = getItemConfig(getRawMenuItems(), baseRoute);
      const routeDescription = menuItem?.title ?? route?.name ?? '';
      const scopesWithOrg = substituteOrgIdInScope(currentOrg, resolvedScope);
      const missingScopeDescriptions = scopeCodesToDescriptions(scopesWithOrg);
      return (
        <NotFoundPage
          routeDescription={routeDescription}
          missingScopeDescriptions={missingScopeDescriptions}
        />
      );
    }

    debugLog('We are past the loading check!');

    // wrap everything in suspense (and each component is wrapped in lazy() so that we can allow downloads of bundles dynamically for faster load time)
    return (
      <Suspense
        key={route.name}
        fallback={
          <div style={{ marginTop: '55px' }}>
            <Loader loaderContentOnly />
          </div>
        }
      >
        <ChosenRoute {...originalProps} route={route} extraData={extraData} />
      </Suspense>
    );
  },
  (prev, curr) => isEqual(prev, curr),
);

WrappedRoute.propTypes = {
  RouteComponent: PropTypes.oneOfType([
    PropTypes.shape(),
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.shape()),
  ]),
  RouteImportPath: PropTypes.string,
  originalProps: PropTypes.shape(),
  route: PropTypes.shape({
    route: PropTypes.object,
    name: PropTypes.string.isRequired,
  }),
  reducers: PropTypes.array,
  sagas: PropTypes.array,
  scope: PropTypes.array,
  debug: PropTypes.bool,
  noAuth: PropTypes.bool,
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  name: PropTypes.string,
  extraData: PropTypes.object,
};

WrappedRoute.displayName = 'WrappedRoute';
